import React, { useEffect } from 'react'
import get from 'lodash.get'
import CopyToClipboard from 'copy-to-clipboard'
import {message, notification} from 'antd'
import gql from 'graphql-tag'
import {useLazyQuery} from '@apollo/react-hooks'
import Button from 'components/shared/Button'
import './index.scss'

const getDownloadUrlQuery = gql`
  query getVideoDownloadUrl($productId: ID!, $url: String!) {
    product(id: $productId) {
      id
      ... on AssetsBasedProduct {
        videoDownloadUrl(url: $url)
      }
    }
  }
`

const TEXTS = {
  SlideshowProduct: {
    product: 'SoMe',
    watchProduct: 'Se SoMe',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til SoMe.',
      'Du kan dele den med hvem du vil.'
    ],
  },
  ParallaxVideoProduct: {
    product: 'Parallax Video',
    watchProduct: 'Se Parallax Video',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til Parallax Video.',
      'Du kan dele den med hvem du vil.'
    ],
  },
  VideoProduct: {
    product: 'video',
    watchProduct: 'Se video',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til videoen din.',
      'Du kan dele den med hvem du vil.'
    ],
  },
  SlidesProduct: {
    product: 'Slides',
    watchProduct: 'Se slides',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til slides.',
      'Du kan dele den med hvem du vil.',
    ],
  },
  VisoProduct: {
    product: 'Viso',
    watchProduct: 'Se Viso',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til Viso.',
      'Husk at den må publiseres før du får en offentlig url du kan dele.',
    ],
  },
  WalkthroughProduct: {
    product: '360 Visning',
    watchProduct: 'Se 360 Visning',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til 360 Visning.',
      'Du kan dele den med hvem du vil.',
    ],
  },
  StylingProduct: {
    product: 'Interiørveiledning',
    watchProduct: 'Se rapport',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her kan du se og laste ned rapporten fra interiørveilederen.',
    ],
  },
  DigitalStylingProduct: {
    product: 'Online Interiørveiledning',
    watchProduct: 'Se video fra møte',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her kan du se møtet fra interiørveilederen.',
    ],
  },
  EnergyLabelingProduct: {
    product: 'Energimerking',
    watchProduct: 'Se rapport',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her kan du se og laste ned energimerkingsrapport.',
    ],
  },
  VirtualTourProduct: {
    product: 'Digital visning',
    watchProduct: 'Se digital visning',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til din digitale visning.',
      <><b>Merk:</b> Lenken til produktet digital visning er aktiv i 3 måneder fra leveringstidspunkt. Ved behov for forlenget levetid, ta kontakt med <a href="mailto:ks@efkt.com">ks@efkt.com</a>.</>
    ],
  },
  Drone360Product: {
    product: 'Drone 360 - komplett',
    watchProduct: 'Se drone 360',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til din drone 360.',
    ],
  },
  Object360Product: {
    product: 'Orbit',
    watchProduct: 'Se Orbit',
    copiedLink: 'Lenke er kopiert',
    descriptions: [
      'Her har du en lenke til Orbit.',
    ],
  },
}

function defaultGetDeliveredUrl(product) {
  return get(product, 'assets.edges[0].node.url')
}

function defaultGetTexts(product) {
  return TEXTS[product.__typename]
}

const DeliveredUrl = ({product, getDeliveredUrl = defaultGetDeliveredUrl, getTexts = defaultGetTexts}) => {
  const [getDownloadUrl, {loading, data, error}] = useLazyQuery(getDownloadUrlQuery, {fetchPolicy: 'no-cache'})

  const url = getDeliveredUrl(product)
  const texts = getTexts(product)

  // Should really live in backend, but the VideoProduct resolver dont have the url
  // so lets just do it here...
  const canDownloadVideo = url?.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1]

  useEffect(() => {
    if (!data && !error) return

    const downloadUrl = data?.product.videoDownloadUrl
    if (error || !downloadUrl) {
      notification['error']({
        message: 'Nedlastning feilet',
        description: 'Kunne ikke laste ned video grunnet en feil. Forsøk på nytt senere.',
      })
      return
    }
    window.location = downloadUrl
    message.info('Nedlastning har startet')
  }, [data, error])

  const copyToClipboard = () => {
    if (CopyToClipboard(url)) {
      message.info(texts.copiedLink)
    }
  }

  const onClickDownload = () => {
    return getDownloadUrl({
      variables: {
        url,
        productId: product.id,
      }
    })
  }

  if (!url) {
    return (
      <div className="delivered-url">
        <p>Mangler URL for {texts.product}</p>
      </div>
    )
  }
  return (
    <div className="delivered-url">
      {texts.descriptions.map((text, i) => (
        <p key={i}>{text}</p>
      ))}

      <div className="actions">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Button
            block
            type="action"
          >
            {texts.watchProduct}
          </Button>
        </a>
        <Button
          block
          secondary
          type="action"
          onClick={copyToClipboard}
        >
          Kopier lenke
        </Button>
        {canDownloadVideo && (
          <Button
            block
            secondary
            type="action"
            disabled={loading}
            onClick={onClickDownload}
          >
            Last ned video
          </Button>
        )}
      </div>
    </div>
  )
}

export default DeliveredUrl
